// useResource: one async read with loading, error, refresh time, reload, and
// an optional poll. Every screen loads through it, so B2 gets polling and
// 401 handling in one place.
import { useCallback, useContext, useEffect, useRef, useState, createContext } from "react";
import { isUnauthorized } from "./api";

/** Raised to the shell when any read or write comes back 401. */
export const UnauthorizedContext = createContext<() => void>(() => {});

export function useOnUnauthorized(): () => void {
  return useContext(UnauthorizedContext);
}

export interface Resource<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  /** Client time of the last successful load. */
  refreshedAt: Date | null;
  reload: () => void;
}

export interface ResourceOptions {
  /** Reload every n ms while mounted (Ringkasan and Kiriman poll every 10 s, visible tab only). */
  pollMs?: number;
  /** Keep polling at pollMs while the tab is hidden (Koneksi's 10 s rate). */
  pollHidden?: boolean;
}

export function useResource<T>(load: () => Promise<T>, deps: readonly unknown[], options: ResourceOptions = {}): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);
  const onUnauthorized = useOnUnauthorized();
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let live = true;
    setLoading(true);
    loadRef.current().then(
      (value) => {
        if (!live) return;
        setData(value);
        setError(null);
        setLoading(false);
        setRefreshedAt(new Date());
      },
      (err: unknown) => {
        if (!live) return;
        if (isUnauthorized(err)) onUnauthorized();
        setError(err);
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
    // deps are the caller's query keys; tick is a manual or polled reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  useEffect(() => {
    if (!options.pollMs) return;
    // Visible-only unless pollHidden: a hidden tab does not poll, and
    // becoming visible refetches once.
    const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const id = setInterval(() => {
      if (options.pollHidden || !hidden()) setTick((t) => t + 1);
    }, options.pollMs);
    const onVisibility = () => {
      if (!hidden()) setTick((t) => t + 1);
    };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [options.pollMs, options.pollHidden]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, refreshedAt, reload };
}

/** True once `on` has stayed true for `ms`, so a fast load never flashes a skeleton. */
export function useDelayed(on: boolean, ms = 300): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!on) {
      setShown(false);
      return;
    }
    const id = setTimeout(() => setShown(true), ms);
    return () => clearTimeout(id);
  }, [on, ms]);
  return shown;
}
